// =============================================================
// BTR Companion — hook data maison (pas de react-query)
// Cache mémoire partagé + dédup des requêtes en vol + états loading/error.
// =============================================================
import { useState, useEffect, useRef, useCallback } from 'react';

const cache = new Map();    // key -> { data, ts }
const inflight = new Map(); // key -> Promise
const TTL = 60_000;         // 60s

/**
 * useApi(key, fetcher, options)
 *  - key : string|null. Si null, la requête est désactivée (ex: champ vide).
 *  - fetcher : () => Promise<data>
 *  - options.ttl : durée de validité du cache (ms)
 * Retourne { data, loading, error, refetch }.
 */
export function useApi(key, fetcher, { ttl = TTL } = {}) {
  const [state, setState] = useState(() => {
    const hit = key && cache.get(key);
    return hit && Date.now() - hit.ts < ttl
      ? { data: hit.data, loading: false, error: null }
      : { data: hit?.data ?? null, loading: !!key, error: null };
  });

  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const run = useCallback(
    (force = false) => {
      if (!key) {
        setState({ data: null, loading: false, error: null });
        return;
      }
      const hit = cache.get(key);
      if (!force && hit && Date.now() - hit.ts < ttl) {
        setState({ data: hit.data, loading: false, error: null });
        return;
      }
      setState((s) => ({ ...s, loading: true, error: null }));
      let p = !force && inflight.get(key);
      if (!p) {
        p = Promise.resolve().then(() => fetcherRef.current());
        inflight.set(key, p);
        p.finally(() => {
          if (inflight.get(key) === p) inflight.delete(key);
        });
      }
      p.then(
        (data) => {
          cache.set(key, { data, ts: Date.now() });
          setState({ data, loading: false, error: null });
        },
        (error) => setState((s) => ({ data: s.data, loading: false, error })),
      );
    },
    [key, ttl],
  );

  useEffect(() => {
    let alive = true;
    if (!key) {
      setState({ data: null, loading: false, error: null });
      return;
    }
    const hit = cache.get(key);
    if (hit && Date.now() - hit.ts < ttl) {
      setState({ data: hit.data, loading: false, error: null });
    } else {
      // run() gère son propre setState ; on évite juste les updates post-unmount
      setState((s) => ({ ...s, loading: true, error: null }));
      (inflight.get(key) || Promise.resolve().then(() => fetcherRef.current()))
        .then((data) => {
          cache.set(key, { data, ts: Date.now() });
          if (alive) setState({ data, loading: false, error: null });
        })
        .catch((error) => alive && setState((s) => ({ data: s.data, loading: false, error })));
    }
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, ttl]);

  return { ...state, refetch: () => run(true) };
}

export default useApi;
